import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Mail, Trash2 } from "lucide-react";
import {
  MessageBodyCacheBadge,
  type MessageBodyCacheStatus,
} from "@/features/inbox/MessageBodyCacheBadge";
import { SenderAvatar } from "@/features/shared/SenderAvatar";
import { cn } from "@/lib/cn";
import { extractSenderLabel, formatMailListDate, mailListPeerLabel } from "@/lib/formatMailDate";

const ACTION_WIDTH = 80;
const SWIPE_COMMIT = 68;
const LONG_PRESS_MS = 560;
const AXIS_LOCK_PX = 16;
const TAP_SLOP_PX = 14;
const EXIT_MS = 320;

/** Résistance élastique au-delà de la largeur d’action. */
function rubberBandOffset(dx: number): number {
  const limit = ACTION_WIDTH;
  if (dx >= 0) {
    return dx <= limit ? dx : limit + (dx - limit) * 0.22;
  }
  const abs = Math.abs(dx);
  return abs <= limit ? dx : -(limit + (abs - limit) * 0.22);
}

function swipeProgress(offset: number): { unread: number; delete: number } {
  return {
    unread: Math.min(1, Math.max(0, offset) / ACTION_WIDTH),
    delete: Math.min(1, Math.max(0, -offset) / ACTION_WIDTH),
  };
}

type Props = {
  subject: string;
  from: string;
  to?: string;
  preferRecipient?: boolean;
  date: string;
  seen: boolean;
  hasAttachments?: boolean;
  bodyCacheStatus?: MessageBodyCacheStatus;
  showBodyCacheBadge?: boolean;
  /** False = nuage / pas encore DD02 — tap bloqué (aligné desktop). */
  bodyOpenable?: boolean;
  checked?: boolean;
  showCheckbox?: boolean;
  onSelect: () => void;
  onToggleCheck?: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
  onLongPress: () => void;
  /** Précharge le corps dès le toucher (cache local). */
  onPrefetch?: () => void;
  /** Animation visuelle seule (suppression barre d’outils). */
  pendingExit?: boolean;
};

export function MobileSwipeableMessageRow({
  subject,
  from,
  to,
  preferRecipient = false,
  date,
  seen,
  hasAttachments,
  bodyCacheStatus = "idle",
  showBodyCacheBadge = true,
  bodyOpenable = true,
  checked = false,
  showCheckbox = false,
  onSelect,
  onToggleCheck,
  onMarkUnread,
  onDelete,
  onLongPress,
  onPrefetch,
  pendingExit = false,
}: Props) {
  const peer = mailListPeerLabel({ from, to, preferRecipient });
  const sender = peer.label;
  const avatarLabel = extractSenderLabel(peer.avatarRaw);
  const startRef = useRef({ x: 0, y: 0 });
  const axisRef = useRef<"none" | "x" | "y">("none");
  const longPressRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const scrollGestureRef = useRef(false);
  const maxMoveRef = useRef(0);
  const offsetRef = useRef(0);
  const rowRef = useRef<HTMLDivElement>(null);

  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [exiting, setExiting] = useState(false);
  const externalExitRef = useRef(false);

  const setOffsetSafe = useCallback((value: number) => {
    const banded = rubberBandOffset(value);
    const clamped = Math.max(-(rowRef.current?.offsetWidth ?? 420), Math.min(ACTION_WIDTH * 1.35, banded));
    offsetRef.current = clamped;
    setOffset(clamped);
  }, []);

  const clearLongPress = useCallback(() => {
    if (longPressRef.current != null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  const resetGesture = useCallback(() => {
    clearLongPress();
    setDragging(false);
    setExiting(false);
    setOffsetSafe(0);
    axisRef.current = "none";
    scrollGestureRef.current = false;
    maxMoveRef.current = 0;
  }, [clearLongPress, setOffsetSafe]);

  const playExitAnimation = useCallback(
    (onComplete?: () => void) => {
      const width = rowRef.current?.offsetWidth ?? 360;
      setDragging(false);
      setExiting(true);
      offsetRef.current = -width;
      setOffset(-width);
      if (navigator.vibrate) {
        navigator.vibrate([8, 24, 12]);
      }
      window.setTimeout(() => {
        if (onComplete) {
          onComplete();
          resetGesture();
        }
      }, EXIT_MS);
    },
    [resetGesture]
  );

  const playDeleteExit = useCallback(() => {
    playExitAnimation(onDelete);
  }, [onDelete, playExitAnimation]);

  useEffect(() => {
    if (!pendingExit || externalExitRef.current) return;
    externalExitRef.current = true;
    playExitAnimation();
  }, [pendingExit, playExitAnimation]);

  const onTouchStart = (e: React.TouchEvent) => {
    onPrefetch?.();
    const touch = e.touches[0];
    if (!touch) return;
    startRef.current = { x: touch.clientX, y: touch.clientY };
    axisRef.current = "none";
    longPressFiredRef.current = false;
    scrollGestureRef.current = false;
    maxMoveRef.current = 0;
    clearLongPress();
    longPressRef.current = window.setTimeout(() => {
      if (scrollGestureRef.current || maxMoveRef.current > TAP_SLOP_PX) return;
      longPressFiredRef.current = true;
      onLongPress();
      if (navigator.vibrate) {
        navigator.vibrate(12);
      }
    }, LONG_PRESS_MS);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    const dx = touch.clientX - startRef.current.x;
    const dy = touch.clientY - startRef.current.y;
    maxMoveRef.current = Math.max(maxMoveRef.current, Math.abs(dx), Math.abs(dy));

    if (maxMoveRef.current > TAP_SLOP_PX) {
      clearLongPress();
    }

    if (axisRef.current === "none") {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;

      if (Math.abs(dy) >= Math.abs(dx)) {
        axisRef.current = "y";
        scrollGestureRef.current = true;
        return;
      }

      axisRef.current = "x";
      setDragging(true);
    }

    if (axisRef.current === "y" || scrollGestureRef.current) {
      return;
    }

    e.preventDefault();
    setOffsetSafe(dx);
  };

  const onTouchEnd = () => {
    clearLongPress();
    setDragging(false);

    if (longPressFiredRef.current) {
      resetGesture();
      longPressFiredRef.current = false;
      return;
    }

    if (scrollGestureRef.current || axisRef.current === "y") {
      resetGesture();
      return;
    }

    const current = offsetRef.current;
    if (current >= SWIPE_COMMIT) {
      setOffsetSafe(ACTION_WIDTH * 0.55);
      onMarkUnread();
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
      window.setTimeout(() => resetGesture(), 180);
      return;
    }
    if (current <= -SWIPE_COMMIT) {
      playDeleteExit();
      return;
    }

    if (axisRef.current === "x") {
      resetGesture();
      return;
    }

    if (maxMoveRef.current <= TAP_SLOP_PX && bodyOpenable) {
      onSelect();
    }
    resetGesture();
  };

  const onTouchCancel = () => {
    longPressFiredRef.current = false;
    resetGesture();
  };

  const progress = swipeProgress(offset);
  const multiSelect = showCheckbox;
  const showUnreadBadge = !seen && !checked;
  const rowTransition = dragging
    ? undefined
    : exiting
      ? `transform ${EXIT_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${EXIT_MS}ms ease-out`
      : "transform 0.38s cubic-bezier(0.32, 0.72, 0, 1)";

  return (
    <div ref={rowRef} className="relative overflow-hidden">
      <div
        className="mobile-swipe-action mobile-swipe-action--unread pointer-events-none absolute inset-y-0 left-0 flex w-[5rem] items-center justify-center gap-1.5"
        style={{
          opacity: progress.unread > 0 ? 0.45 + progress.unread * 0.55 : 0,
          visibility: progress.unread > 0 ? "visible" : "hidden",
          transform: `scale(${0.88 + progress.unread * 0.12})`,
        }}
        aria-hidden
      >
        <Mail
          className="h-4 w-4 transition-transform duration-150"
          style={{ transform: `scale(${0.9 + progress.unread * 0.2})` }}
        />
        <span className="text-[10px] font-semibold">Non lu</span>
      </div>
      <div
        className="mobile-swipe-action mobile-swipe-action--delete pointer-events-none absolute inset-y-0 right-0 flex w-[5rem] items-center justify-center gap-1.5"
        style={{
          opacity: progress.delete > 0 ? 0.4 + progress.delete * 0.6 : 0,
          visibility: progress.delete > 0 ? "visible" : "hidden",
          transform: `scale(${0.88 + progress.delete * 0.12})`,
        }}
        aria-hidden
      >
        <Trash2
          className="h-4 w-4 transition-transform duration-150"
          style={{ transform: `scale(${0.9 + progress.delete * 0.25})` }}
        />
        <span
          className="text-[10px] font-semibold transition-opacity duration-150"
          style={{ opacity: 0.6 + progress.delete * 0.4 }}
        >
          Suppr.
        </span>
      </div>

      <div
        className={cn(
          "relative flex w-full gap-3 border-b border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-4 py-3 text-left mobile-swipe-row",
          seen
            ? undefined
            : "border-l-[3px] border-l-[var(--mail-accent)] bg-[var(--mail-surface-elevated)] pl-[calc(1rem-3px)]",
          dragging && Math.abs(offset) > 12 && "mobile-swipe-row--active",
          !bodyOpenable && showBodyCacheBadge && "opacity-70"
        )}
        style={{
          transform: offset !== 0 ? `translateX(${offset}px)` : undefined,
          opacity: exiting ? 0 : undefined,
          transition: rowTransition,
        }}
        aria-disabled={!bodyOpenable}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        {multiSelect ? (
          <button
            type="button"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              onToggleCheck?.();
            }}
            aria-label={checked ? "Désélectionner" : "Sélectionner"}
            aria-pressed={checked}
            className={cn(
              "group/circle relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2",
              checked ? "bg-[var(--mail-accent)] text-white shadow-sm" : ""
            )}
          >
            {checked ? (
              <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
            ) : (
              <>
                <SenderAvatar
                  label={avatarLabel}
                  size="md"
                  className="transition-opacity duration-150 group-hover/circle:opacity-0"
                />
                <span
                  className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-[var(--mail-accent)] text-white opacity-0 shadow-sm transition-opacity duration-150 group-active/circle:opacity-100"
                  aria-hidden
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
              </>
            )}
          </button>
        ) : (
          <SenderAvatar label={avatarLabel} size="md" />
        )}

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                "min-w-0 truncate text-sm",
                seen ? "font-medium text-[var(--mail-muted)]" : "font-semibold text-[var(--mail-text)]"
              )}
            >
              {sender}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              {showBodyCacheBadge ? (
                <MessageBodyCacheBadge status={bodyCacheStatus} />
              ) : null}
              <span className="text-[11px] text-[var(--mail-muted)]">
                {formatMailListDate(date)}
              </span>
            </span>
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            {showUnreadBadge ? (
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--mail-accent)]" aria-hidden />
            ) : null}
            <span
              className={cn(
                "min-w-0 truncate text-sm",
                seen ? "text-[var(--mail-muted)]" : "font-medium text-[var(--mail-text)]"
              )}
            >
              {subject || "(Sans objet)"}
            </span>
            {hasAttachments ? (
              <span className="shrink-0 text-[10px] text-[var(--mail-muted)]" aria-label="Pièces jointes">
                📎
              </span>
            ) : null}
          </span>
        </span>
      </div>
    </div>
  );
}
